/**
 * SOP content-envelope helpers (pure, exported for unit tests).
 *
 * SOP Galaxy ships each SOP's body as an envelope:
 *   content = { structured, plain_text, html }
 * where `structured` is the author's raw editor shape — one of:
 *   - { type: 'text', content: '<md>' }               legacy flat text
 *   - { type: 'structured', header, sections, ... }   structured template
 *   - { type: 'doc', content: [...] }                 TipTap document
 *   - { type: 'tiptap-block', doc: {...} }            block editor (the DEFAULT)
 *   - a bare array / unknown shape
 *
 * The SOPs Library renderer has dedicated branches only for the first two. For
 * every OTHER shape it must fall back to the already-rendered `html` /
 * `plain_text` that travel in the SAME envelope — otherwise the body renders
 * blank (the reported "metadata shows but content is empty" bug for SOPs
 * authored in the default block editor).
 */

/** True when `structured` is the section-based template the detailed renderer
 *  understands — ONLY `type: 'structured'` (or, defensively, a legacy row that
 *  carries a `sections` map but predates the explicit `type`).
 *
 *  A `header` must NOT count: a Word import (`tiptap-block`) also carries a
 *  `header` (its SOP number) but has NO sections, so treating a header as a
 *  template is exactly what made Word SOPs render only their SOP number. A
 *  tiptap-block never has `sections`, so it correctly falls through to the
 *  html/plain_text fallback. */
export function isStructuredTemplate(structured) {
  if (!structured || typeof structured !== 'object') return false
  if (structured.type === 'structured') return true
  return Boolean(structured.sections && typeof structured.sections === 'object')
}

/** The rendered-body fallback for a rich/unknown `structured` shape: prefer the
 *  envelope's rendered HTML, else its flattened plain text. Returns
 *  `{ html, text }` (either may be ''). Pure — the caller sanitizes `html`
 *  before injecting it. */
export function sopContentFallback(content) {
  const html =
    content && typeof content.html === 'string' ? content.html.trim() : ''
  const text =
    content && typeof content.plain_text === 'string'
      ? content.plain_text.trim()
      : ''
  return { html, text }
}
